import api from './api';
import { Camera } from '../interfaces';
import axios from 'axios';
import i18n from '../i18n';

function getStreamingApiUrl(): string {
  if (process.env.REACT_APP_STREAMING_API_URL) {
    return process.env.REACT_APP_STREAMING_API_URL;
  }

  const isProduction = process.env.NODE_ENV === 'production';

  if (isProduction) {
    return '';
  }

  return 'http://localhost:3001';
}

const STREAMING_API_URL = getStreamingApiUrl();
console.log("Usando servidor de streaming:", STREAMING_API_URL || "[Caminhos relativos]");

function getUserId(): string {
  try {
    const userStr = localStorage.getItem('user');

    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        if (userData && userData.userId !== undefined) {
          return String(userData.userId);
        }
      } catch (e) {}
    }

    const userId = localStorage.getItem('userId');
    if (userId) return userId;

    const token = localStorage.getItem('token');
    if (token) {
      try {
        const base64Payload = token.split('.')[1];
        const payload = JSON.parse(atob(base64Payload));

        if (payload && payload.userId) {
          localStorage.setItem('userId', String(payload.userId));
          return String(payload.userId);
        }
      } catch (e) {
        console.error("Erro ao extrair userId do token:", e);
      }
    }

    return "1";
  } catch (error) {
    console.error(">> ERRO AO OBTER USERID:", error);
    return "1";
  }
}

interface HlsAvailabilityResponse {
  available: boolean;
  reason?: string;
  segments?: number;
  lastSegmentAge?: number;
  currentRecorder?: string;
  currentChannel?: number;
  hlsPath?: string;
}

interface StreamInfoResponse {
  success: boolean;
  streaming: boolean;
  streamInfo?: {
    rtspUrl: string;
    recorder: string;
    channel: number;
    startTime: string;
    userId?: string;
    hlsDir?: string;
    hlsPlaylistPath?: string;
  };
  message?: string;
  uptime?: number;
  hlsPath?: string;
}

interface PrepareStreamResult {
  success: boolean;
  url: string | null;
  error?: string;
  errorMessage?: string;
}

function getErrorMessage(error: any): string {
  if (axios.isAxiosError(error)) {
    return (
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      i18n.t("stream.commError")
    );
  }

  return error?.message || i18n.t("stream.unexpectedError");
}
function getHlsReasonMessage(reason?: string): string {
  switch (reason) {
    case "request-failed":
      return i18n.t("stream.hlsRequestFailed")

    case "no-stream-info":
      return i18n.t("stream.hlsGenerateError")

    case "no-playlist":
      return i18n.t("stream.hlsGenerateError")

    case "no-segments":
      return i18n.t("stream.hlsGenerateError")

    case "segments-too-old":
      return i18n.t("stream.hlsRecoverError")

    case "error-checking-segments":
      return i18n.t("stream.hlsRecoverError")

    default:
      return i18n.t("stream.cameraVideoError")
  }
}

export interface SnapshotParams {
  rtspUser: string
  rtspPass: string
  recorderExtIP: string
  recorderPort?: string
  channel: number
  recorderTypeName?: string
}

export const streamService = {
  /**
   * Busca um único frame JPEG da câmera via /api/snapshot.
   * Retorna uma object URL pronta para uso em <img src>.
   * Lembre de revogar a URL anterior (URL.revokeObjectURL) antes de chamar novamente.
   */
  getSnapshot: async (params: SnapshotParams): Promise<string> => {
    const q = new URLSearchParams({
      rtspUser: params.rtspUser,
      rtspPass: params.rtspPass,
      recorderExtIP: params.recorderExtIP,
      recorderPort: params.recorderPort || '554',
      channel: String(params.channel),
      recorderTypeName: params.recorderTypeName || 'Hikvision',
    })
    const response = await axios.get(`${STREAMING_API_URL}/streams/api/snapshot?${q}`, {
      responseType: 'blob',
      timeout: 20000,
    })
    return URL.createObjectURL(response.data)
  },

  startStream: async (
    recorder?: string,
    channel?: number,
    recorderExtIP?: string,
    recorderPort?: string,
    tenantId?: number,
    rtspUser?: string,
    rtspPass?: string,
    recorderTypeName?: string
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();

      if (recorder) params.append('recorder', recorder);
      if (channel) params.append('channel', channel.toString());

      const userId = getUserId();
      params.append('userId', userId);

      if (tenantId) params.append('tenant_id', tenantId.toString());
      if (recorderExtIP) params.append('recorderExtIP', recorderExtIP);
      if (recorderPort) params.append('recorderPort', recorderPort);
      if (rtspUser) params.append('rtspUser', rtspUser);
      if (rtspPass) params.append('rtspPass', rtspPass);
      if (recorderTypeName) params.append('recorderTypeName', recorderTypeName);

      const url = `${STREAMING_API_URL}/streams/api/start-stream?${params.toString()}`;

      console.log(">> 🔵 PARAMETROS ENVIADOS:");
      console.log(">>   - recorder:", recorder);
      console.log(">>   - channel:", channel);
      console.log(">>   - recorderExtIP:", recorderExtIP);
      console.log(">>   - recorderPort:", recorderPort);
      console.log(">>   - tenantId:", tenantId);
      console.log(">>   - rtspUser:", rtspUser ? "***" : "não enviado");
      console.log(">>   - rtspPass:", rtspPass ? '***' + rtspPass.slice(-3) : 'não enviado');
      console.log(">>   - recorderTypeName:", recorderTypeName);

      const response = await axios.get(url);

      console.log(">> 🟢 RESPOSTA DO SERVIDOR:", response.data);

      return response.data;
    } catch (error) {
      console.error(">> ERRO AO INICIAR STREAM:", error);
      throw error;
    }
  },

  stopStream: async (tenantId?: number): Promise<any> => {
    try {
      const userId = getUserId();
      const params = new URLSearchParams();

      params.append('userId', userId);

      if (tenantId) {
        params.append('tenant_id', tenantId.toString());
      }

      const response = await axios.get(
        `${STREAMING_API_URL}/streams/api/stop-stream?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      console.error("Erro ao parar stream:", error);
      throw error;
    }
  },

  getStreamInfo: async (tenantId?: number): Promise<StreamInfoResponse> => {
    try {
      const userId = getUserId();
      const params = new URLSearchParams();

      params.append('userId', userId);

      if (tenantId) {
        params.append('tenant_id', tenantId.toString());
      }

      const response = await axios.get(
        `${STREAMING_API_URL}/streams/api/stream-info?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      console.error("Erro ao obter informações do stream:", error);

      return {
        success: false,
        streaming: false,
        message: i18n.t("stream.infoError"),
      };
    }
  },

  checkHlsAvailability: async (tenantId?: number): Promise<HlsAvailabilityResponse> => {
    try {
      const userId = getUserId();
      const params = new URLSearchParams();

      params.append('userId', userId);

      if (tenantId) {
        params.append('tenant_id', tenantId.toString());
      }

      // Anti-cache: força 200 OK e evita resposta 304 cacheada (igual ao app Flutter)
      params.append('_t', Date.now().toString());

      const response = await axios.get(
        `${STREAMING_API_URL}/streams/api/check-hls?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      console.error('>> ERRO AO VERIFICAR DISPONIBILIDADE HLS:', error);
      return { available: false, reason: 'request-failed' };
    }
  },

  getHlsUrl: (): string => {
    const userId = getUserId();
    return `${STREAMING_API_URL}/streams/hls/${userId}/playlist.m3u8`;
  },

  /**
   * Conecta um stream a partir de um gravador + canal, sem depender de uma
   * câmera já salva. Usado na configuração da câmera (inclusive nova) para
   * exibir a imagem ao vivo e desenhar a região de interesse.
   */
  prepareStreamFromRecorder: async (
    recorderId: number,
    channel: number,
    tenant_id: number = 1,
  ): Promise<PrepareStreamResult> => {
    try {
      const recResponse = await api.get(`/recorder/${tenant_id}/${recorderId}`)
      const rec = recResponse.data?.recorder || recResponse.data

      if (!rec) {
        return {
          success: false,
          url: null,
          error: "recorder_not_found",
          errorMessage: i18n.t("stream.recorderNotFoundCreds"),
        }
      }

      const recorderExtIP = rec.recorderExtIP || rec.recorder_ext_ip
      const recorderPort = rec.recorderExtRTSP ? String(rec.recorderExtRTSP) : "554"
      const rtspUser = rec.recorderUser || rec.recorder_rtsp_user
      const rtspPass = rec.recorderPass || rec.recorder_rtsp_pass
      const recorderTypeName = String(rec.recorderTypeName || rec.recorder_type_name || "Hikvision")
      const recorderName = String(rec.recorderName || rec.name || recorderId)

      if (!rtspUser || !rtspPass) {
        return {
          success: false,
          url: null,
          error: "rtsp_credentials_missing",
          errorMessage: i18n.t("stream.rtspCredentialsMissingRecorder"),
        }
      }

      if (!recorderExtIP || recorderExtIP === "127.0.0.1") {
        return {
          success: false,
          url: null,
          error: "recorder_external_ip_missing",
          errorMessage: i18n.t("stream.recorderExternalIpMissing"),
        }
      }

      const startResult = await streamService.startStream(
        recorderName,
        channel,
        recorderExtIP,
        recorderPort,
        tenant_id,
        rtspUser,
        rtspPass,
        recorderTypeName,
      )

      if (!startResult.success) {
        return {
          success: false,
          url: null,
          error: "start_failed",
          errorMessage:
            startResult.message ||
            startResult.error ||
            i18n.t("stream.startFailedChannel"),
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 5000))

      let available = false
      let attempts = 0
      const maxAttempts = 15 // ~30s de margem para cold start (espelha o app Flutter)
      let lastResponse: HlsAvailabilityResponse | null = null

      while (!available && attempts < maxAttempts) {
        const response = await streamService.checkHlsAvailability(tenant_id)
        lastResponse = response
        available = response.available
        if (available) break
        const waitTime = 2000 // polling fixo de 2s entre checagens (igual ao Flutter)
        await new Promise((resolve) => setTimeout(resolve, waitTime))
        attempts++
      }

      if (!available) {
        return {
          success: false,
          url: null,
          error: "hls_unavailable",
          errorMessage: getHlsReasonMessage(lastResponse?.reason),
        }
      }

      const timestamp = Date.now()
      let hlsUrl: string
      if (lastResponse && lastResponse.hlsPath) {
        let fixedPath = lastResponse.hlsPath
        if (fixedPath.startsWith("/hls/")) fixedPath = `/streams${fixedPath}`
        hlsUrl = `${STREAMING_API_URL}${fixedPath}?_t=${timestamp}`
      } else {
        hlsUrl = `${streamService.getHlsUrl()}?_t=${timestamp}`
      }

      return { success: true, url: hlsUrl }
    } catch (error: any) {
      return {
        success: false,
        url: null,
        error: error?.message || "unknown_error",
        errorMessage: getErrorMessage(error),
      }
    }
  },

  prepareStreamData: async (
    camera: Camera,
    recorder: any | null,
    tenant_id: number = 1
  ): Promise<PrepareStreamResult> => {
    try {
      if (camera.cameraStatus !== 1) {
        console.log(`Câmera ${camera.cameraId} (${camera.cameraName}) não está online`);
        return {
          success: false,
          url: null,
          error: 'camera_offline',
          errorMessage: i18n.t("stream.cameraOffline", { name: camera.cameraName }),
        };
      }

      console.log("=== PREPARANDO STREAM ===");
      console.log("Câmera:", camera.cameraName);
      console.log("Tenant ID:", tenant_id);
      console.log("Camera ID:", camera.cameraId);

      let cameraData: any;
      let recorderExtIP: string;
      let recorderPort: string;
      let rtspUser: string;
      let rtspPass: string;
      let recorderTypeName: string;

      try {
        console.log(`>> BUSCANDO DADOS COMPLETOS: /camera/${tenant_id}/${camera.cameraId}`);

        const cameraResponse = await api.get(`/camera/${tenant_id}/${camera.cameraId}`);

        console.log(`>> RESPOSTA DA API:`, cameraResponse.data);

        if (!cameraResponse.data || !cameraResponse.data.camera) {
          throw new Error(i18n.t('stream.apiInvalidResponse'));
        }

        cameraData = cameraResponse.data.camera;

        console.log('>> TODOS OS CAMPOS DO CAMERADATA:', JSON.stringify(cameraData, null, 2));

        recorderExtIP =
          cameraData.recorderExtIP ||
          cameraData.recorder_ext_ip ||
          cameraData.externalIp;

        recorderPort = cameraData.recorderExtRTSP
          ? String(cameraData.recorderExtRTSP)
          : '554';

        rtspUser =
          cameraData.recorderUser ||
          cameraData.recorder_rtsp_user ||
          cameraData.rtspUser;

        rtspPass =
          cameraData.recorderPass ||
          cameraData.recorder_rtsp_pass ||
          cameraData.rtspPass;

        recorderTypeName = String(
          cameraData.recorderTypeName ||
          cameraData.recorder_type_name ||
          camera.recorderTypeName ||
          'Hikvision'
        );

        console.log('>> ✅ DADOS OBTIDOS DA API:');
        console.log('>>   - IP:', recorderExtIP);
        console.log('>>   - Porta:', recorderPort);
        console.log('>>   - User:', rtspUser ? '***' : 'não definido');
        console.log('>>   - Pass:', rtspPass ? '***' + rtspPass.slice(-3) : 'não definido');
        console.log('>>   - Tipo:', recorderTypeName);
      } catch (error) {
        console.error(`>> ❌ ERRO AO BUSCAR DADOS DA CÂMERA:`, error);
        return {
          success: false,
          url: null,
          error: 'camera_data_failed',
          errorMessage: getErrorMessage(error) || i18n.t("stream.cameraDataFailed"),
        };
      }

      if (!cameraData) {
        return {
          success: false,
          url: null,
          error: 'camera_data_missing',
          errorMessage: i18n.t("stream.cameraDataMissing"),
        };
      }

      if (!rtspUser || !rtspPass) {
        return {
          success: false,
          url: null,
          error: 'rtsp_credentials_missing',
          errorMessage: i18n.t("stream.rtspCredentialsMissingCamera"),
        };
      }

      if (!recorderExtIP || recorderExtIP === '127.0.0.1') {
        return {
          success: false,
          url: null,
          error: 'recorder_external_ip_missing',
          errorMessage: i18n.t("stream.recorderExternalIpMissing"),
        };
      }

      const recorderName = String(
        cameraData.recorderName ||
        camera.recorderName ||
        cameraData.cameraRecorder ||
        camera.cameraRecorder ||
        camera.cameraId
      );

      const channelNumber = Number(
        cameraData.cameraChannel ||
        camera.cameraChannel ||
        1
      );

      console.log('>> RESUMO DOS PARÂMETROS:');
      console.log(`>>   - Câmera: ${camera.cameraName}`);
      console.log(`>>   - Gravador: ${recorderName}`);
      console.log(`>>   - Tipo: ${recorderTypeName}`);
      console.log(`>>   - Canal: ${channelNumber}`);
      console.log(`>>   - IP: ${recorderExtIP}`);
      console.log(`>>   - Porta: ${recorderPort}`);
      console.log(`>>   - TenantId: ${tenant_id}`);
      console.log(`>>   - User: ${rtspUser ? '***' : 'não definido'}`);
      console.log(`>>   - Pass: ${rtspPass ? '***' + rtspPass.slice(-3) : 'não definido'}`);

      const startResult = await streamService.startStream(
        recorderName,
        channelNumber,
        recorderExtIP,
        recorderPort,
        tenant_id,
        rtspUser,
        rtspPass,
        recorderTypeName
      );

      if (!startResult.success) {
        console.error("Falha ao iniciar stream:", startResult);
        return {
          success: false,
          url: null,
          error: 'start_failed',
          errorMessage:
            startResult.message ||
            startResult.error ||
            i18n.t("stream.startFailedCamera"),
        };
      }

      console.log("Aguardando inicialização do processo RTSP->HLS...");
      await new Promise(resolve => setTimeout(resolve, 5000));

      let available = false;
      let attempts = 0;
      const maxAttempts = 15; // ~30s de margem para cold start (espelha o app Flutter)
      let lastResponse: HlsAvailabilityResponse | null = null;

      while (!available && attempts < maxAttempts) {
  try {
    const response = await streamService.checkHlsAvailability(tenant_id)
    lastResponse = response
    available = response.available

    if (available) {
      break
    }

    const reason = response.reason || "unknown"

    console.warn(">> HLS ainda indisponível:", reason)

    const fatalReasons = [
      "ffmpeg-exited",
      "rtsp-failed",
      "camera-offline",
      "connection-refused",
      "auth-failed",
      "invalid-rtsp",
      "no-signal",
    ]

    if (fatalReasons.includes(reason)) {
      return {
        success: false,
        url: null,
        error: reason,
        errorMessage: getHlsReasonMessage(reason),
      }
    }

    const waitTime = 2000 // polling fixo de 2s entre checagens (igual ao Flutter)
    await new Promise(resolve => setTimeout(resolve, waitTime))
    attempts++
  } catch (error) {
    console.warn("Erro ao verificar disponibilidade do HLS:", error)
    await new Promise(resolve => setTimeout(resolve, 2000))
    attempts++
  }
}

      if (!available) {
        console.error("HLS não disponível após várias tentativas:", lastResponse);
        return {
          success: false,
          url: null,
          error: 'hls_unavailable',
          errorMessage: getHlsReasonMessage(lastResponse?.reason),
        };
      }

      const timestamp = Date.now();
      let hlsUrl: string;

      if (lastResponse && lastResponse.hlsPath) {
        let fixedPath = lastResponse.hlsPath;

        if (fixedPath.startsWith('/hls/')) {
          fixedPath = `/streams${fixedPath}`;
        }

        hlsUrl = `${STREAMING_API_URL}${fixedPath}?_t=${timestamp}`;

        console.log(`>> CAMINHO ORIGINAL HLS: ${lastResponse.hlsPath}`);
        console.log(`>> CAMINHO AJUSTADO HLS: ${fixedPath}`);
      } else {
        hlsUrl = `${streamService.getHlsUrl()}?_t=${timestamp}`;
      }

      console.log(`>> ✅ STREAM HLS DISPONÍVEL`);
      console.log(`>> URL COMPLETA: ${hlsUrl}`);
      console.log("=== FIM PREPARAÇÃO STREAM ===");

      return { success: true, url: hlsUrl };
    } catch (error: any) {
      console.error("❌ Erro ao preparar dados do stream:", error);
      return {
        success: false,
        url: null,
        error: error?.message || 'unknown_error',
        errorMessage: getErrorMessage(error),
      };
    }
  },
};